import { useQuery } from 'react-query';

import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import { LANGUAGES } from '@savgroup-front-common/constants/shared';
import { intl } from '@savgroup-front-common/core/helpers';
import { CUSTOMER_TYPE } from '@savgroup-front-common/types';
import { ClientService } from '@/api';
import { AuthConfiguration } from '@/configuration';
import { ClientInformation } from '@/types';

export const GET_CLIENT_INFORMATION = 'getClientInformation';

interface UseGetClientInformationReturnValues {
  clientInformation?: ClientInformation;
  sellerPartners?: {
    sellerId: string;
    sellerName: string;
    customerType?: CUSTOMER_TYPE;
  }[];
  currentLanguage: LANGUAGES;
  isLoading: boolean;
}

const useGetClientInformation = (): UseGetClientInformationReturnValues => {
  const { clientId } = AuthConfiguration;

  const { data, isLoading } = useQuery(
    [
      GET_CLIENT_INFORMATION,
      {
        clientId,
        locale: intl?.locale,
      },
    ],
    async () => {
      if (!clientId) {
        return undefined;
      }
      const responseClientInformation =
        await ClientService.getClientInformations({
          clientId,
        });
      const responseSellerPartners = await ClientService.getSellerPartners({
        clientId,
      });

      if (responseClientInformation.failure || responseSellerPartners.failure) {
        logError(
          `Got an empty getClientInformation for clientId: ${clientId}.`,
        );

        return undefined;
      }

      return {
        clientInformation: responseClientInformation.value,
        sellerPartners: responseSellerPartners.value,
      };
    },
    {
      staleTime: Infinity,
    },
  );

  return {
    clientInformation: data?.clientInformation,
    sellerPartners: data?.sellerPartners,
    currentLanguage: intl?.locale as LANGUAGES,
    isLoading,
  };
};

export default useGetClientInformation;
